import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import WistiaPlayer from "../components/WistiaPlayer";
import CaseStudyHero from "../components/CaseStudyHero";
import AnimatedNumber from "../components/AnimatedNumber";
import { breadcrumbSchema } from "../lib/schemas";

// Logo cell con anti-duplicazione: nasconde il singolo logo se l'immagine non esiste
const BrandLogoCell = ({ brand }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <div className="w-full aspect-[3/2] flex items-center justify-center grayscale hover:grayscale-0 transition">
      <img
        src={`/images/works/problemi-di-volley/logos/${brand}.png`}
        alt={brand}
        loading="lazy"
        onError={() => setFailed(true)}
        className="max-w-[140px] max-h-[80px] object-contain"
        data-testid={`brand-logo-${brand}`}
      />
    </div>
  );
};

// Card per case correlati
const RelatedCard = ({ to, title, eyebrow }) => (
  <Link
    to={to}
    className="block border border-dodgers/15 p-6 md:p-7 hover:border-lime transition-colors group"
    data-testid={`related-${to.replace(/[^a-z0-9]+/g, "-")}`}
  >
    <p className="font-condensed uppercase tracking-widest text-[12px] text-mandrogni mb-2">
      {eyebrow}
    </p>
    <p className="font-display text-[20px] md:text-[22px] leading-tight group-hover:text-lime transition-colors">
      {title}
    </p>
    <span className="inline-block mt-4 font-condensed uppercase tracking-widest text-[12px] text-dodgers/60">
      Vedi il case →
    </span>
  </Link>
);

const CreditRow = ({ role, who }) => (
  <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr] gap-2 md:gap-6 py-3 border-b border-dodgers/10">
    <p className="font-condensed uppercase tracking-widest text-[12px] text-mandrogni">{role}</p>
    <p className="text-[15px] md:text-[16px] leading-[1.6]">{who}</p>
  </div>
);

const WorkProblemiDiVolley = () => {
  const brands = [
    "sportnet",
    "decathlon",
    "lvf",
    "mizuno",
    "credem",
  ];

  const breadcrumbJsonLd = breadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Works", url: "/works" },
    { name: "Problemi di Volley", url: "/works/problemi-di-volley" },
  ]);

  return (
    <>
      <Seo
        title="Problemi di Volley: 290K follower community OnSports | Case"
        description="Problemi di Volley è la community property OnSports dal 2019: 200K+ Facebook, 87K+ Instagram. Lungolinea (podcast), Casa Cisolla × Credem Banca."
        path="/works/problemi-di-volley"
        image="/images/works/problemi-di-volley/hero.jpg"
        jsonLd={breadcrumbJsonLd}
      />

      {/* SEZ. 1 — HERO */}
      <CaseStudyHero
        imagePath="/images/works/problemi-di-volley/hero.jpg"
        imageAlt="Problemi di Volley — la community della pallavolo italiana"
        eyebrow="● Case study · Cultural Impact · Business Impact"
        titleLines={[
          "Problemi di Volley",
          "La community madre, l'IP di",
          "ONSPORTS.",
        ]}
        highlightWord="ONSPORTS."
        subtitle="La più grande community italiana dedicata alla pallavolo. Testata giornalistica, media company, piattaforma di branded content. Dal 2019 il nostro asset editoriale proprietario."
        meta="2019 — In corso  ·  Property OnSports  ·  Format — Editoriale + Branded Content  ·  Reach — 290K+ follower complessivi"
      />

      {/* SEZ. 2 — OVERVIEW */}
      <section className="bg-galactico text-dodgers py-16 md:py-24" data-testid="section-overview">
        <div className="container-os max-w-[760px] mx-auto">
          <RevealOnScroll>
            <SectionEyebrow>● Overview</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-6 max-w-[24ch]">
              La più grande community italiana dedicata alla pallavolo. La possediamo noi.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <div className="mt-8 space-y-6 text-[17px] leading-[1.7] text-mandrogni">
              <p>
                Problemi di Volley nasce con un'idea precisa: raccontare la pallavolo italiana —
                dalla Serie A ai campionati di provincia — con passione, ironia e senza prendersi
                troppo sul serio. Negli anni cresce fino a diventare la più grande community
                digitale italiana dedicata al volley: 200.000+ follower su Facebook, 87.000+ su
                Instagram, una testata giornalistica registrata, una community attiva di giocatori,
                allenatori, addetti ai lavori e appassionati.
              </p>
              <p>
                Nel 2019 i suoi founder Tommaso e Luca contribuiscono a fondare OnSports e
                inaugurano una divisione dedicata a Branded Content &amp; Advertising. Problemi di
                Volley diventa così la property editoriale proprietaria di OnSports — un asset
                distintivo che ci permette di proporre ai brand qualcosa che nessun'altra agenzia
                italiana può offrire: un canale di distribuzione editoriale già esistente, una
                community già attiva, una voce credibile dentro il mondo del volley.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 3 — PERCHÉ È STRATEGICA PER I BRAND */}
      <section className="bg-dodgers text-galactico py-20 md:py-28" data-testid="section-valore-brand">
        <div className="container-os max-w-[760px] mx-auto">
          <RevealOnScroll>
            <p className="font-condensed uppercase tracking-widest text-[12px] text-lime">
              ● Il valore per i brand
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-6 max-w-[26ch]">
              Non un canale dove comprare visibilità. Una community dove costruire significato.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <div className="mt-8 space-y-6 text-[17px] leading-[1.7] text-galactico/80">
              <p>
                La maggior parte delle agenzie di sports marketing porta il messaggio del brand a un
                pubblico. Problemi di Volley ne è il punto di partenza opposto: la community esiste
                già, ha la sua voce, i suoi codici, la sua credibilità. Quando un brand si attiva su
                PdV, non sta comprando un'inserzione — sta entrando in una conversazione che è già
                in corso, dove il pubblico riconosce la voce della community e quindi accorda
                fiducia ai contenuti.
              </p>
              <p>
                Da qui i format di branded content che produciamo: Casa Cisolla con Credem Banca,
                Extra Ordinary con Credem, attivazioni con Reale Mutua, content con Mizuno,
                partnership con Kappa × Coop. Ogni progetto è costruito sulla grammatica nativa di
                PdV — non come "pubblicità in vesti editoriali", ma come contenuti che la community
                percepisce come propri.
              </p>
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 4 — I BRAND CHE CI HANNO SCELTO */}
      <section className="bg-galactico text-dodgers py-20 md:py-28" data-testid="section-brand-partner">
        <div className="container-os max-w-[1100px] mx-auto">
          <RevealOnScroll>
            <SectionEyebrow>● Brand partner</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-6 max-w-[26ch]">
              Chi ha costruito storytelling sulla nostra community.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-8 items-center justify-items-center max-w-[900px] mx-auto py-12">
              {brands.map((brand) => (
                <BrandLogoCell key={brand} brand={brand} />
              ))}
            </div>
          </RevealOnScroll>
          <RevealOnScroll delay={0.18}>
            <p className="text-center text-[14px] italic text-mandrogni leading-[1.6] max-w-[60ch] mx-auto">
              Solo alcuni dei brand attivati sulla community PdV negli ultimi 5 anni.
            </p>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 5 — LUNGOLINEA — IL PODCAST */}
      <section className="bg-dodgers text-galactico py-20 md:py-28" data-testid="section-lungolinea">
        <div className="container-os max-w-[760px] mx-auto">
          <RevealOnScroll>
            <p className="font-condensed uppercase tracking-widest text-[12px] text-lime">
              ● Format flagship · Lungolinea
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-6 max-w-[26ch]">
              Il podcast della pallavolo. Non frettoloso. Lungo come una linea di fondo.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <div className="mt-8 space-y-6 text-[17px] leading-[1.7] text-galactico/80">
              <p>
                Lungolinea nasce nel 2020 con un'idea anti-formato: chiacchierate lunghe e
                approfondite con giocatori, giocatrici, allenatori e personaggi del Volley.
                L'opposto delle interviste post-partita di 30 secondi. Negli anni il podcast ha
                ospitato voci di primo piano del movimento — Andrea Zorzi, Anna Danesi, Bruninho,
                Paolo Nicolai, Salvatore Rossini, Angelo Lorenzetti, Matteo Piano, e molti altri.
              </p>
              <p>
                Quello che lo distingue è il tono: niente domande standard, niente celebrazione
                retorica. Conversazione vera, anche dura quando serve, su cosa significa fare
                pallavolo a livello professionistico. Per la community PdV è il format più amato.
                Per i brand partner è il contenuto più desiderabile: chi sponsorizza Lungolinea
                associa il proprio marchio non a "un podcast di nicchia", ma a una conversazione
                autorevole sul movimento.
              </p>
            </div>
          </RevealOnScroll>
        </div>

        <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          <div className="max-w-[380px] mx-auto w-full">
            <WistiaPlayer mediaId="oejepdchwp" aspect={9 / 16} />
          </div>
          <div className="max-w-[380px] mx-auto w-full">
            <WistiaPlayer mediaId="nia4ylw2xw" aspect={9 / 16} />
          </div>
          <div className="max-w-[380px] mx-auto w-full">
            <WistiaPlayer mediaId="uwjol1m2rt" aspect={9 / 16} />
          </div>
        </div>
        <p className="mt-6 text-center text-[14px] italic text-galactico/60 max-w-[60ch] mx-auto px-6">
          Tre estratti dal podcast Lungolinea distribuiti sui canali PdV.
        </p>
      </section>

      {/* SEZ. 6 — CASA CISOLLA × CREDEM */}
      <section className="bg-galactico text-dodgers py-20 md:py-28" data-testid="section-casa-cisolla">
        <div className="container-os max-w-[760px] mx-auto">
          <RevealOnScroll>
            <SectionEyebrow>● Format flagship · Casa Cisolla × Credem</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-6 max-w-[26ch]">
              Il salotto buono della pallavolo. Title partner: Credem Banca.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <div className="mt-8 space-y-6 text-[17px] leading-[1.7] text-mandrogni">
              <p>
                Casa Cisolla è un format video sviluppato da Problemi di Volley insieme a Credem
                Banca, title sponsor della Lega Pallavolo Serie A. Host del format è Alberto Cisolla
                — ex-capitano della Nazionale volley maschile, campione d'Europa e argento Olimpico
                — affiancato da Tommy di Problemi di Volley come partner-in-crime.
              </p>
              <p>
                Ogni puntata è una chiacchierata informale con un protagonista del volley italiano,
                condita da aneddoti di carriera, giochi rocamboleschi e domande inviate dalla
                community PdV. Tra gli ospiti delle stagioni: Andrea Zorzi, Simone Buti, Marco
                Mordente, Giulia Momoli, Noemi Signorile, Anna Danesi e altri. Il format dimostra
                come la sponsorship di Credem non sia una semplice esposizione di logo, ma uno
                spazio editoriale costruito sulla credibilità della community PdV.
              </p>
            </div>
          </RevealOnScroll>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
          {["rUHFfsxhlYg", "7G63c1B0k-0", "MaJ7DeHmWhk"].map((vid) => (
            <div
              key={vid}
              className="aspect-video rounded overflow-hidden border border-dodgers/10"
              data-testid={`casa-cisolla-${vid}`}
            >
              <iframe
                src={`https://www.youtube.com/embed/${vid}?rel=0&modestbranding=1`}
                title="Casa Cisolla — Episodio"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-[14px] italic text-mandrogni max-w-[60ch] mx-auto px-6">
          Tre estratti delle stagioni di Casa Cisolla.
        </p>
      </section>

      {/* SEZ. 7 — L'IMPATTO */}
      <section className="bg-dodgers text-galactico py-24 md:py-28" data-testid="section-impatto">
        <div className="container-os max-w-[1100px] mx-auto text-center">
          <RevealOnScroll>
            <p className="font-condensed uppercase tracking-widest text-[12px] text-lime">
              ● L'impatto
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="display-h2 mt-6 max-w-[22ch] mx-auto">
              Una community che vale <span className="text-lime">CENTOMILA</span> voci.
            </h2>
          </RevealOnScroll>

          <RevealOnScroll delay={0.12}>
            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-10 items-end">
              <div className="min-w-0 text-center md:text-left" data-testid="kpi-facebook">
                <AnimatedNumber
                  end={200}
                  suffix="K+"
                  className="font-display text-lime block leading-[0.9] text-[64px] md:text-[80px] lg:text-[96px] tracking-tight"
                />
                <p className="font-condensed uppercase tracking-widest text-[14px] text-galactico/80 mt-4">
                  Follower Facebook
                </p>
              </div>
              <div className="min-w-0 text-center md:text-left" data-testid="kpi-instagram">
                <AnimatedNumber
                  end={87}
                  suffix="K+"
                  className="font-display text-lime block leading-[0.9] text-[64px] md:text-[80px] lg:text-[96px] tracking-tight"
                />
                <p className="font-condensed uppercase tracking-widest text-[14px] text-galactico/80 mt-4">
                  Follower Instagram
                </p>
              </div>
              <div className="min-w-0 text-center md:text-left" data-testid="kpi-anni">
                <AnimatedNumber
                  end={6}
                  suffix="+ anni"
                  className="font-display text-lime block leading-[0.9] text-[64px] md:text-[80px] lg:text-[96px] tracking-tight"
                />
                <p className="font-condensed uppercase tracking-widest text-[14px] text-galactico/80 mt-4">
                  Di pubblicazione editoriale
                </p>
              </div>
            </div>
          </RevealOnScroll>

          <RevealOnScroll delay={0.2}>
            <p className="mt-16 text-[17px] leading-[1.7] text-galactico/80 max-w-[760px] mx-auto">
              Numeri che certificano una posizione di mercato unica: nessun'altra realtà italiana ha
              costruito intorno alla pallavolo una community editoriale di queste dimensioni. Per
              OnSports e per i brand che si attivano su PdV, questo significa accesso diretto al
              pubblico più appassionato e qualificato del volley italiano — senza passare da
              intermediari, broadcaster o tornei.
            </p>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 8 — CREDITS */}
      <section className="bg-galactico text-dodgers py-12 md:py-16" data-testid="section-credits">
        <div className="container-os max-w-[720px] mx-auto">
          <RevealOnScroll>
            <SectionEyebrow>● Credits</SectionEyebrow>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2
              className="font-display mt-6 leading-tight"
              style={{ fontSize: "clamp(28px, 3.4vw, 36px)" }}
            >
              Chi fa cosa.
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.12}>
            <div className="mt-8 border-t border-dodgers/10">
              <CreditRow role="Founders Problemi di Volley" who="Tommaso & Luca" />
              <CreditRow role="Editorial Direction" who="Team PdV" />
              <CreditRow role="Branded Content production" who="OnSports + Team PdV" />
              <CreditRow role="Lungolinea (podcast)" who="Team PdV" />
              <CreditRow role="Casa Cisolla — Host" who="Alberto Cisolla" />
              <CreditRow role="Casa Cisolla — Partner" who="Credem Banca + OnSports" />
              <CreditRow role="Property" who="OnSports (Media Company interna)" />
              <CreditRow role="Periodo" who="2019 — In corso" />
            </div>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 9 — CTA + NAVIGATION */}
      <section className="bg-dodgers text-galactico py-20 md:py-28" data-testid="section-cta">
        <div className="container-os max-w-[760px] mx-auto text-center">
          <RevealOnScroll>
            <h2 className="display-h2 max-w-[24ch] mx-auto">
              Vuoi attivarti sulla community? <span className="text-lime">PARLIAMONE.</span>
            </h2>
          </RevealOnScroll>
          <RevealOnScroll delay={0.08}>
            <p className="mt-6 text-[17px] leading-[1.7] text-galactico/80 max-w-[60ch] mx-auto">
              Una call iniziale di 30 minuti per capire se la nostra community è il pubblico giusto
              per il tuo brand.
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.14}>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                as={Link}
                to="/contatti"
                variant="primary"
                testId="pdv-cta-contatti"
              >
                Parliamone
              </Button>
              <Button
                as={Link}
                to="/works?service=content-ip-studio"
                variant="secondary-light"
                testId="pdv-cta-altri-content"
              >
                Vedi altri case Content &amp; IP
              </Button>
            </div>
          </RevealOnScroll>
        </div>

        <div className="container-os max-w-[1100px] mx-auto mt-20 md:mt-24">
          <RevealOnScroll>
            <p className="font-condensed uppercase tracking-widest text-[12px] text-lime mb-6">
              ● Case correlati
            </p>
          </RevealOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <RelatedCard
              to="/works/credem-extra-ordinary"
              eyebrow="Social Impact · Content"
              title="Credem × Extra Ordinary"
            />
            <RelatedCard
              to="/works/reale-mutua-essere-libero"
              eyebrow="Cultural Impact · Content"
              title="Reale Mutua × Essere Libero"
            />
            <RelatedCard
              to="/works/mizuno-valentina-zago"
              eyebrow="Cultural Impact · Brand Legacy"
              title="Mizuno × Valentina Zago"
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default WorkProblemiDiVolley;
